import React from 'react';
import { Campaign, CampaignStatus } from '../../types/campaign.types';
import { formatNumber } from '../../utils/formatters';

interface CampaignCardProps {
    campaign: Campaign;
    onView: (id: number) => void;
    onExecute?: (id: number) => void;
    onStop?: (id: number) => void;
    onDelete?: (id: number) => void;
}

const statusColors: Record<CampaignStatus, string> = {
    [CampaignStatus.DRAFT]: 'bg-slate-100 text-slate-700 ring-slate-200',
    [CampaignStatus.PROCESSING]: 'bg-blue-100 text-blue-700 ring-blue-200',
    [CampaignStatus.IN_PROGRESS]: 'bg-amber-100 text-amber-700 ring-amber-200',
    [CampaignStatus.COMPLETED]: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
    [CampaignStatus.FAILED]: 'bg-red-100 text-red-700 ring-red-200',
    [CampaignStatus.CANCELLED]: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const CampaignCard: React.FC<CampaignCardProps> = ({
    campaign,
    onView,
    onExecute,
    onStop,
    onDelete,
}) => {
    const canExecute = !campaign.is_active && campaign.total_pending > 0;
    const canDelete = !campaign.is_active;
    const revenue = `${campaign.currency} ${formatNumber(campaign.actual_revenue || 0)}`;
    const attempted = (campaign.total_sent || 0) + (campaign.total_failed || 0);
    const progress = campaign.total_contacts > 0
        ? Math.min(100, Math.round((attempted / campaign.total_contacts) * 100))
        : 0;

    const canStop = campaign.status === CampaignStatus.PROCESSING || campaign.status === CampaignStatus.IN_PROGRESS;

    return (
        <div className="grid grid-cols-1 gap-4 px-4 py-4 transition hover:bg-white/70 md:grid-cols-[minmax(220px,1.8fr)_160px_130px_130px_140px] md:items-center md:px-6">
            <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => onView(campaign.id)}
                        className="truncate text-left text-sm font-semibold text-slate-950 hover:text-blue-700"
                    >
                        {campaign.name}
                    </button>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${statusColors[campaign.status]}`}>
                        {campaign.status.replace('_', ' ').toUpperCase()}
                    </span>
                </div>
                <p className="mt-2 text-xs text-slate-500 md:hidden">
                    {formatNumber(campaign.total_contacts)} contacts · {formatNumber(campaign.total_sent)} sent · {formatNumber(campaign.total_failed)} failed
                </p>
            </div>

            <div className="text-sm">
                <div className="flex flex-col gap-1">
                    <span className="font-semibold text-slate-950">Progress: {progress}%</span>
                    <span className="text-xs text-slate-500">
                        {formatNumber(attempted)} of {formatNumber(campaign.total_contacts)} contacts processed
                    </span>
                </div>
            </div>

            <div className="text-sm">
                <p className="text-xs font-medium uppercase text-slate-500 md:hidden">Success</p>
                <p className="font-semibold text-slate-950">{campaign.success_rate.toFixed(1)}%</p>
                <p className="text-xs text-slate-500">
                    {formatNumber(campaign.total_sent)} sent / {formatNumber(campaign.total_failed)} failed
                </p>
            </div>

            <div className="text-sm">
                <p className="text-xs font-medium uppercase text-slate-500 md:hidden">Revenue</p>
                <p className="font-semibold text-emerald-700">{revenue}</p>
            </div>

            <div className="flex flex-wrap justify-start gap-2 md:justify-end">
                <button
                    onClick={() => onView(campaign.id)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                    Details
                </button>

                {canExecute && onExecute && (
                    <button
                        onClick={() => onExecute(campaign.id)}
                        className="rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
                    >
                        {campaign.status === CampaignStatus.DRAFT ? 'Execute' : 'Send Pending'}
                    </button>
                )}

                {canStop && onStop && (
                    <button
                        onClick={() => onStop(campaign.id)}
                        className="rounded-lg border border-amber-600 bg-amber-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
                    >
                        Stop
                    </button>
                )}

                {canDelete && onDelete && (
                    <button
                        onClick={() => onDelete(campaign.id)}
                        className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50"
                    >
                        Delete
                    </button>
                )}
            </div>
        </div>
    );
};

export default CampaignCard;
